<script setup lang="ts">
/** Section « Demande » — type, niveau d'urgence, temps de traitement estimé. */
import { ClipboardDocumentListIcon } from '@heroicons/vue/24/outline'
import type {
  AccompRequestType,
  AccompUrgency,
} from '@/v2/accompagnement/api/accompagnementTypes'
import {
  ACCOMP_TYPE_OPTIONS,
  ACCOMP_URGENCY_OPTIONS,
} from '@/v2/accompagnement/constants/accompagnementConstants'

defineProps<{ typeError?: string }>()

const requestType = defineModel<AccompRequestType | ''>('requestType', { required: true })
const urgency = defineModel<AccompUrgency>('urgency', { required: true })
const estimatedHours = defineModel<number | null>('estimatedHours', { required: true })

function onHoursInput(e: Event) {
  const raw = (e.target as HTMLInputElement).value
  estimatedHours.value = raw === '' ? null : Math.max(0, Number(raw))
}
</script>

<template>
  <section class="card border border-base-300 bg-base-100 shadow-sm" aria-labelledby="accomp-demande-title">
    <div class="card-body gap-4 p-4 md:p-6">
      <h2 id="accomp-demande-title" class="card-title flex items-center gap-2 text-lg font-semibold">
        <ClipboardDocumentListIcon class="h-5 w-5 text-primary" aria-hidden="true" />
        Demande
      </h2>

      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <!-- Type -->
        <div>
          <label for="accomp-type" class="mb-1 block text-sm font-medium text-base-content">
            Type de demande <span class="text-error" aria-hidden="true">*</span>
          </label>
          <select
            id="accomp-type"
            v-model="requestType"
            class="select select-bordered w-full"
            :class="{ 'select-error': typeError }"
          >
            <option value="" disabled>Sélectionnez un type…</option>
            <option v-for="opt in ACCOMP_TYPE_OPTIONS" :key="opt.value" :value="opt.value">
              {{ opt.label }}
            </option>
          </select>
          <p v-if="typeError" class="mt-1 text-sm text-error">{{ typeError }}</p>
        </div>

        <!-- Temps estimé -->
        <div>
          <label for="accomp-hours" class="mb-1 block text-sm font-medium text-base-content">
            Temps de traitement estimé (heures)
          </label>
          <input
            id="accomp-hours"
            type="number"
            min="0"
            step="0.5"
            class="input input-bordered w-full"
            placeholder="Ex. 4"
            :value="estimatedHours ?? ''"
            @input="onHoursInput"
          />
        </div>

        <!-- Niveau d'urgence -->
        <fieldset class="md:col-span-2">
          <legend class="mb-2 text-sm font-medium text-base-content">Niveau d'urgence</legend>
          <div class="flex flex-wrap gap-2">
            <label
              v-for="opt in ACCOMP_URGENCY_OPTIONS"
              :key="opt.value"
              class="flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2"
              :class="urgency === opt.value ? 'border-primary bg-primary/5' : 'border-base-300'"
            >
              <input v-model="urgency" type="radio" class="radio radio-primary radio-sm" :value="opt.value" />
              <span class="text-base">{{ opt.label }}</span>
            </label>
          </div>
        </fieldset>
      </div>
    </div>
  </section>
</template>
